/**
 * Sprint T-16.1 · Automation Center · Detail panels (drawers).
 *
 * JobDetailPanel · loads /jobs/{id} lazily via useAutomationJob.
 * ExecutionDetailPanel · loads /executions/{id} via useExecutionDetail.
 *
 * Both are controlled (open + onClose) and cover >md screens as right-side
 * drawers · in xs/sm they render as bottom-sheet full-height.
 */
import React from "react";
import { X, Clock } from "lucide-react";
import JsonPreview from "./JsonPreview";
import { StatusBadge, CategoryBadge, PriorityBadge, TriggerBadge, DurationBadge, RetryBadge } from "./Badges";
import { RelativeTime, AbsoluteTime, humanReadableSchedule } from "./Formatters";
import { useAutomationJob, useExecutionDetail } from "../../../hooks/useAutomation";
import { WidgetCard, EmptyState, LoadingState } from "../../advisor";


function DrawerShell({ title, onClose, children, testId }) {
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-[70] flex justify-end bg-black/60"
      onClick={onClose}
    >
      <aside
        className="flex h-full w-full max-w-2xl flex-col overflow-hidden border-l border-[var(--ag-border)] bg-[var(--ag-surface)] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-[var(--ag-border)] px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--ag-text-dim)]">
            {title}
          </span>
          <button
            type="button"
            data-testid={`${testId}-close`}
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] p-1 hover:bg-[var(--ag-surface-2)]"
          >
            <X className="h-3 w-3" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-3">{children}</div>
      </aside>
    </div>
  );
}


// ============================================================
// JobDetailPanel
// ============================================================

export function JobDetailPanel({ jobId, onClose }) {
  const q = useAutomationJob(jobId);
  const data = q.data;
  const job = data?.job;
  const state = data?.state;
  const recent = data?.recent_executions || [];
  const snapshot = data?.latest_snapshot;

  return (
    <DrawerShell title={`Job · ${jobId}`} onClose={onClose} testId="job-detail-panel">
      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState message="No se pudo cargar el job." />}
      {job && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <CategoryBadge category={job.category} />
            <PriorityBadge priority={job.priority} />
            {state?.last_status && <StatusBadge status={state.last_status} />}
          </div>
          <div>
            <h2 className="font-mono text-sm font-semibold">{job.name}</h2>
            <p className="mt-0.5 text-xs text-[var(--ag-text-dim)]">{job.description}</p>
          </div>

          <WidgetCard title="Configuración" testId="widget-job-config">
            <dl className="grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Schedule</dt>
                <dd>{humanReadableSchedule(job.schedule)}</dd>
              </div>
              <div>
                <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Handler</dt>
                <dd className="truncate font-mono text-[10px]" title={job.handler_path}>
                  {job.handler_path}
                </dd>
              </div>
              <div>
                <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Timeout</dt>
                <dd>{job.timeout_seconds}s</dd>
              </div>
              <div>
                <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Retry policy</dt>
                <dd>
                  max_attempts={job.retry_policy?.max_attempts} · base={job.retry_policy?.base_delay_seconds}s ·
                  factor={job.retry_policy?.factor}
                </dd>
              </div>
              {job.depends_on?.length > 0 && (
                <div className="col-span-2">
                  <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">depends_on</dt>
                  <dd className="mt-0.5 flex flex-wrap gap-1">
                    {job.depends_on.map((d) => (
                      <code key={d} className="rounded bg-[var(--ag-surface-2)] px-1 py-0.5 font-mono text-[9px]">
                        {d}
                      </code>
                    ))}
                  </dd>
                </div>
              )}
              {job.emits_events?.length > 0 && (
                <div className="col-span-2">
                  <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">emits_events</dt>
                  <dd className="mt-0.5 flex flex-wrap gap-1">
                    {job.emits_events.map((ev) => (
                      <code key={ev} className="rounded bg-[var(--ag-surface-2)] px-1 py-0.5 font-mono text-[9px]">
                        {ev}
                      </code>
                    ))}
                  </dd>
                </div>
              )}
              {job.citations?.length > 0 && (
                <div className="col-span-2">
                  <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Trazabilidad</dt>
                  <dd className="mt-0.5 flex flex-wrap gap-1">
                    {job.citations.map((c) => (
                      <code key={c} className="rounded bg-[var(--ag-surface-2)] px-1 py-0.5 font-mono text-[9px] text-[var(--ag-text-dim)]">
                        {c}
                      </code>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          </WidgetCard>

          {state && (
            <WidgetCard title="Runtime state" testId="widget-job-state">
              <dl className="grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Última ejecución</dt>
                  <dd><RelativeTime iso={state.last_run_at} /></dd>
                </div>
                <div>
                  <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Último éxito</dt>
                  <dd><RelativeTime iso={state.last_success_at} /></dd>
                </div>
                <div>
                  <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Runs totales</dt>
                  <dd className="font-mono">{state.total_runs}</dd>
                </div>
                <div>
                  <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Fallos consecutivos</dt>
                  <dd className="font-mono">{state.consecutive_failures || 0}</dd>
                </div>
              </dl>
            </WidgetCard>
          )}

          {recent.length > 0 && (
            <WidgetCard title="Últimas 5 ejecuciones" testId="widget-job-recent">
              <ul className="space-y-1 text-[10px]">
                {recent.map((e) => (
                  <li key={e.id} className="flex items-center justify-between rounded border border-[var(--ag-border)] px-2 py-1">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={e.status} />
                      <RetryBadge attempt={e.attempt} max={e.max_attempts} />
                      <TriggerBadge trigger={e.trigger} />
                    </div>
                    <div className="flex items-center gap-2">
                      <DurationBadge ms={e.duration_ms} />
                      <RelativeTime iso={e.finished_at || e.created_at} />
                    </div>
                  </li>
                ))}
              </ul>
            </WidgetCard>
          )}

          {snapshot && (
            <WidgetCard title="Latest snapshot" testId="widget-job-snapshot">
              <div className="text-[10px] text-[var(--ag-text-dim)]">
                Guardado: <AbsoluteTime iso={snapshot.created_at} />
              </div>
              <div className="mt-1">
                <JsonPreview data={snapshot.payload} maxChars={300} testId="job-snapshot-json" label="snapshot" />
              </div>
            </WidgetCard>
          )}
        </div>
      )}
    </DrawerShell>
  );
}


// ============================================================
// ExecutionDetailPanel
// ============================================================

export function ExecutionDetailPanel({ executionId, onClose }) {
  const q = useExecutionDetail(executionId);
  const e = q.data;
  return (
    <DrawerShell title={`Execution · ${executionId?.slice(0, 8)}`} onClose={onClose} testId="execution-detail-panel">
      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState message="No se pudo cargar la ejecución." />}
      {e && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge status={e.status} testId="exec-detail-status" />
            <TriggerBadge trigger={e.trigger} />
            <RetryBadge attempt={e.attempt} max={e.max_attempts} />
          </div>
          <div>
            <div className="font-mono text-xs font-semibold">{e.job_name}</div>
            <div className="mt-0.5 text-[10px] text-[var(--ag-text-dim)]">
              run_id: <code className="font-mono">{e.run_id}</code>
            </div>
            {e.correlation_id && (
              <div className="text-[10px] text-[var(--ag-text-dim)]">
                correlation_id: <code className="font-mono">{e.correlation_id}</code>
              </div>
            )}
          </div>

          <WidgetCard title="Timeline" testId="widget-exec-timeline">
            <ul className="space-y-1 text-[10px]">
              {[
                ["Creado", e.created_at],
                ["Disponible", e.available_at],
                ["Iniciado", e.started_at],
                ["Heartbeat", e.heartbeat_at],
                ["Terminado", e.finished_at],
              ].map(([label, iso]) => (
                <li key={label} className="flex items-center gap-2">
                  <Clock className="h-3 w-3 text-[var(--ag-text-dim)]" />
                  <span className="w-24 text-[var(--ag-text-dim)]">{label}</span>
                  <AbsoluteTime iso={iso} />
                </li>
              ))}
              {e.duration_ms != null && (
                <li className="flex items-center gap-2 pt-1 border-t border-[var(--ag-border)]">
                  <span className="w-24 text-[var(--ag-text-dim)]">Duración</span>
                  <DurationBadge ms={e.duration_ms} />
                </li>
              )}
            </ul>
          </WidgetCard>

          <WidgetCard title="Input" testId="widget-exec-input">
            <JsonPreview data={e.input || {}} maxChars={400} testId="exec-input-json" label="input" />
          </WidgetCard>

          {e.output && (
            <WidgetCard title="Output" testId="widget-exec-output">
              <JsonPreview data={e.output} maxChars={400} testId="exec-output-json" label="output" />
            </WidgetCard>
          )}

          {e.error && (
            <WidgetCard title="Error" testId="widget-exec-error">
              <div className="rounded border border-red-500/30 bg-red-500/5 p-2 text-[10px]">
                <div>
                  <span className="font-mono text-red-500">{e.error.type}</span>
                </div>
                <div className="mt-1 whitespace-pre-wrap">{e.error.message}</div>
                {e.error.stack && (
                  <div className="mt-2">
                    <JsonPreview data={e.error.stack} maxChars={200} testId="exec-error-stack" label="stack trace" />
                  </div>
                )}
              </div>
            </WidgetCard>
          )}
        </div>
      )}
    </DrawerShell>
  );
}
